<template>
	<div class="body_profitability_analysis">
		<div v-if="isLoading" class="state_message"><p>Cargando análisis…</p></div>
		<div v-else-if="error" class="state_message"><p class="error_text">{{ error }}</p></div>
		<template v-else-if="data">
			<div class="cards_row">
				<CategoryMetricCard v-for="card in cards" :key="card.key"
					:label="card.label"
					:amount="card.amount"
					:pills="card.pills"
					:items="card.shareItems" />
			</div>
			<p v-if="!cards.length" class="state_message">
				Sin datos de {{ categoryLabel }} para este mes.
			</p>

			<!-- Con una selección parcial en el select de la categoría principal
				(una tienda o varias), la card pasa a barras de venta + márgenes
				del agregado de esa selección. -->
			<ProfitabilityCategoryEvolution class="evolution"
				:months="evolutionBuckets"
				:period="period"
				:metrics="evolutionMetrics"
				:items="items"
				:selection-label="selectionLabel" />
		</template>
	</div>
</template>

<script>
import { mapState } from "vuex";
import CategoryMetricCard from "./CategoryMetricCard.vue";
import ProfitabilityCategoryEvolution from "./ProfitabilityCategoryEvolution.vue";
import { formatVariation } from "@/utils/percentage";

// Cuerpo del drawer "Análisis de Categorías" (Rentabilidad): lee el módulo
// `profitabilityAnalysis` y reparte slices a cards presentacionales, como
// BodySalesAnalysis. Sin props: el modal pasa `bodyProps: null`.
//
// La card de Ventas lleva los badges del período anterior (MoM / QoQ / YoY,
// la etiqueta la manda el backend) y del mismo período del año anterior (en
// Anual no viaja: sería el mismo badge); la de cada margen, su % sobre
// ventas. Las barras reparten ese monto entre los 3 ítems de mayor magnitud
// más "Otros" con el resto acumulado (el recorte lo hace el backend: con una
// docena de tiendas, una barra por cada una era ilegible). El backend manda
// `share` como fracción signada; acá va a 0-100.
const SALES_KEY = "ventas";
// Un color por ítem, estable entre cards: el orden de `items` lo fija el
// backend (por venta del mes) y es el mismo que usa la evolución.
const ITEM_COLORS = [
	"var(--progress-bar-1)",
	"var(--progress-bar-2)",
	"var(--progress-bar-3)",
	"var(--progress-bar-4)",
	"var(--progress-bar-5)",
];
// El resto acumulado no es un ítem: neutro propio, fuera de la rueda de
// colores (el backend lo marca con `is_rest`, no por su label).
const REST_COLOR = "var(--progress-bar-otros)";

export default {
	name: "BodyProfitabilityAnalysis",
	components: { CategoryMetricCard, ProfitabilityCategoryEvolution },

	computed: {
		...mapState("profitabilityAnalysis", [
			"data", "isLoading", "error", "period", "category", "selectedItems",
		]),
		...mapState("classification", ["itemsByCategory"]),

		items() {
			return (this.data && this.data.items) || [];
		},

		// Etiqueta de la selección PARCIAL del select de la categoría
		// principal (Steve, 2026-10-01): con una tienda, su nombre; con
		// varias, "3 tiendas". Con todas tildadas (= sin filtro) es null y la
		// evolución vuelve a una línea por ítem. El plural es el nombre de la
		// categoría tal cual lo escribió el analista.
		selectionLabel() {
			const values = (this.selectedItems && this.selectedItems[this.category]) || [];
			const all = (this.itemsByCategory && this.itemsByCategory[this.category]) || [];
			if (!values.length || values.length >= all.length) return null;
			return values.length === 1 ? values[0] : `${values.length} ${this.category}`;
		},

		evolutionBuckets() {
			return (this.data && this.data.evolution && this.data.evolution.buckets) || [];
		},

		evolutionMetrics() {
			return (this.data && this.data.evolution && this.data.evolution.metrics) || [];
		},

		categoryLabel() {
			return (this.data && this.data.category_label) || "la categoría";
		},

		// Color por ítem, por nombre: la misma tienda conserva su color en las
		// cuatro cards aunque su posición cambie de un escalón a otro.
		colorByItem() {
			return this.items.reduce((acc, name, i) => {
				acc[name] = ITEM_COLORS[i % ITEM_COLORS.length];
				return acc;
			}, {});
		},

		cards() {
			const cards = (this.data && this.data.cards) || [];
			const headline = (this.data && this.data.headline) || {};
			return cards.map((card) => ({
				key: card.key,
				label: card.label,
				amount: card.amount,
				pills: card.key === SALES_KEY
					? this.salesPills(headline)
					// El % de un escalón se lee sobre las ventas del mes: en una
					// cascada de márgenes no hay otro denominador posible.
					: [{ text: `${Math.round(card.percent)}%`, tone: "accent" }],
				shareItems: (card.items || []).map((item) => ({
					label: item.name,
					percent: Math.round(item.share * 100),
					color: item.is_rest ? REST_COLOR : this.colorByItem[item.name],
					isRest: Boolean(item.is_rest),
				})),
			}));
		},
	},

	methods: {
		// Badge de un delta: el semáforo de variaciones del reporte en vez del
		// cyan neutro — una caída se tiene que leer como caída. Sin comparable
		// el badge queda neutro con su guion.
		variationPill(ratio, suffix) {
			let tone = "accent";
			if (ratio > 0) tone = "positive";
			else if (ratio < 0) tone = "negative";
			return { text: `${formatVariation(ratio)} ${suffix}`, tone };
		},

		// Período anterior siempre; el mismo período del año anterior sólo en
		// Mensual y Trimestral (en Anual es el mismo comparable).
		salesPills(headline) {
			const pills = [this.variationPill(headline.prev, headline.prev_label || "MoM")];
			if (this.period !== "year") pills.push(this.variationPill(headline.yoy, "YoY"));
			return pills;
		},
	},
};
</script>

<style scoped lang="scss">
.body_profitability_analysis {
	padding: 0 20px 24px;
}

// Los escalones son una cascada que se lee en fila: una columna por card,
// todas del mismo ancho (nada de `auto-fit`, que con 4 cards y un mínimo alto
// desbordaba el drawer y recortaba la primera).
.cards_row {
	display: grid;
	grid-template-columns: repeat(4, minmax(0, 1fr));
	gap: 16px;
	align-items: stretch;
}

@media (max-width: 1180px) {
	.cards_row {
		grid-template-columns: repeat(2, minmax(0, 1fr));
	}
}

@media (max-width: 680px) {
	.cards_row {
		grid-template-columns: minmax(0, 1fr);
	}
}

.evolution {
	margin-top: 16px;
}

.state_message {
	padding: 32px 0;
	text-align: center;
	color: var(--report-text-muted);
	font-size: 14px;
}

.error_text {
	color: var(--report-negative);
}
</style>
